import React, { useState, useMemo, useEffect, ReactNode, FC } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
  styled,
  useMediaQuery,
  Pagination,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { getDisplayLabel, getTestName, isNotesOrDisclaimer } from '../utils/reports';
import { DisplayDetailsForValue } from '../types/reports';
import useBackendApi from '../hooks/useBackendApi';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import ReportDescriptionDrawer from './ReportDescriptionDrawer';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import { DETECTED } from '../constants/report';
import { CompoundCardProps, CompoundCardsListProps, CompoundMetaData } from '../types/reports';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import CompoundLabelChip from './details/CompoundLabelChip';

const backendURL = process.env.REACT_APP_BACKEND_URL;

const StyledTableContainer = styled(TableContainer)(() => ({
  boxShadow: 'none',
  borderTopRightRadius: '12px',
  borderTopLeftRadius: '12px',
  overflow: 'visible',
})) as typeof TableContainer;

interface ReportLabelTooltipProps extends Omit<TooltipProps, 'title'> {
  title: ReactNode;
  disableTooltip?: boolean;
}

interface LabelInfo {
  description: string;
}

const ReportDetailsTable = ({ compoundDataList, compoundMetaData }: CompoundCardsListProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(30);
  const labelInfoHelpId = 'pfas_label_info';
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('value');
  const [labelInfo, setLabelInfo] = useState<Record<string, LabelInfo>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);

  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const StyledTableRow = styled(TableRow)(() => ({
    backgroundColor: theme.custom.report.details.resultTable.styledTableRow.backgroundColor,
    '&.MuiTableRow-hover:hover': {
      cursor: 'pointer',
      backgroundColor: theme.custom.report.details.resultTable.styledTableRow.onHover.backgroundColor,
    },
    display: 'table-row',
    '&:last-child td, &:last-child th': {
      borderBottom: 'none',
    },
  }));

  const StyledHeaderCell = styled(TableCell)({
    color: theme.custom.report.details.resultTable.styledHeaderCell.color,
    fontWeight: 500,
    fontSize: '16px',
    borderBottom: '1px solid',
    borderBottomColor: theme.custom.report.details.resultTable.styledHeaderCell.borderBottomColor,
    backgroundColor: theme.custom.report.details.resultTable.styledHeaderCell.backgroundColor,
    padding: '4px 12px',
    width: '15%',
  });

  const StyledBodyCell = styled(TableCell)(() => ({
    backgroundColor: 'inherit',
    display: 'table-cell',
    height: 'inherit',
    padding: '4px 12px',
  }));

  const StyledLevelCell = styled(StyledBodyCell)({
    color: theme.custom.report.details.resultTable.styledLevelCell.color,
    fontSize: '12px',
    fontWeight: 400,
    padding: '4px 20px 4px 12px',
    borderBottom: '1px solid',
    borderBottomColor: theme.custom.report.details.resultTable.styledLevelCell.borderBottomColor,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: '30%',
  });

  const StyledRegularCell = styled(StyledBodyCell)({
    color: theme.custom.report.details.resultTable.styledRegularCell.color,
    fontSize: '12px',
    fontWeight: 400,
    padding: '4px 12px',
    borderBottom: '1px solid',
    borderBottomColor: theme.custom.report.details.resultTable.styledRegularCell.borderBottomColor,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  });

  const PaginationContainer = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'center',
    padding: '24px 16px 24px',
    marginBottom: theme.spacing(2),
    backgroundColor: theme.custom.report.details.resultTable.paginationContainer.backgroundColor,
    borderBottomRightRadius: '12px',
    borderBottomLeftRadius: '12px',
  }));

  const StyledPagination = styled(Pagination)(() => ({
    '& .MuiPagination-ul': {
      justifyContent: 'center',
    },
    '& .MuiPaginationItem-root': {
      borderRadius: '8px',
      width: '40px',
      height: '40px',
      backgroundColor: theme.custom.report.details.resultTable.styledPagination.paginationItemRoot.backgroundColor,
      color: theme.custom.report.details.resultTable.styledPagination.paginationItemRoot.color,
      '&:hover': {
        backgroundColor: theme.custom.report.details.resultTable.styledPagination.onHover.backgroundColor,
      },
      '&.Mui-selected': {
        border: '1px solid',
        borderColor: theme.custom.report.details.resultTable.styledPagination.selected.borderColor,
        color: theme.custom.report.details.resultTable.styledPagination.selected.color,
        backgroundColor: theme.custom.report.details.resultTable.styledPagination.selected.backgroundColor,
        boxShadow: 'none',
      },
    },
  }));

  const ReportLabelTooltip: FC<ReportLabelTooltipProps> = ({ title, children, disableTooltip = false, ...props }) => {
    return (
      <Tooltip
        title={disableTooltip ? '' : <Box>{title}</Box>}
        arrow
        placement="top"
        disableHoverListener={disableTooltip}
        PopperProps={{
          container: document.body,
          modifiers: [
            {
              name: 'preventOverflow',
              options: { boundary: 'window' },
            },
          ],
        }}
        componentsProps={{
          tooltip: {
            sx: {
              minHeight: '24px',
              width: '200px',
              backgroundColor: theme.custom.report.details.resultTable.reportLabelTooltip.tooltip.backgroundColor,
              color: theme.custom.report.details.resultTable.reportLabelTooltip.tooltip.color,
              fontSize: '0.875rem',
              padding: '8px 12px',
              borderRadius: '15px',
              boxShadow: theme.custom.report.details.resultTable.reportLabelTooltip.tooltip.boxShadowColor,
            },
          },
          arrow: {
            sx: {
              color: theme.custom.report.details.resultTable.reportLabelTooltip.tooltip.arrow.color,
            },
          },
        }}
        {...props}
      >
        {children}
      </Tooltip>
    );
  };

  const renderReportLabelChipWithTooltip = (ddfv: DisplayDetailsForValue, labelInformation: LabelInfo) => {
    const label = getDisplayLabel(ddfv.label)
    return (
      <ReportLabelTooltip
        title={
          <div>
            <Typography
              sx={{
                color: theme.custom.report.details.resultTable.reportLabelTooltip.tooltip.color,
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '16px',
                marginBottom: '4px',
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                color: theme.custom.report.details.resultTable.reportLabelTooltip.tooltip.color,
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
              }}
            >
              {labelInformation.description}
            </Typography>
          </div>
        }
        disableTooltip={ddfv.label === DETECTED} // Tooltip to be not shown for Detected Label
      >
        <span>
          <CompoundLabelChip
            label={label}
            backgroundColor={theme.custom.report.dataDecoration[ddfv.detectionStatus].backgroundColor}
            fontColor={theme.custom.report.dataDecoration[ddfv.detectionStatus].fillFontColor}
          />
        </span>
      </ReportLabelTooltip>
    );
  };

  const renderTableBodyRows = (
    dataReady: boolean,
    visibleRows: CompoundCardProps[],
    compoundMetaData: Record<string, CompoundMetaData>,
    labelInfo: Record<string, LabelInfo>,
    handleOpen: (ddfv: DisplayDetailsForValue) => void,
  ) => {
    if (!dataReady) {
      return (
        <StyledTableRow>
          <StyledBodyCell colSpan={7} align="center">
            <GradientCircularProgressContainer loadingMessage="Loading details..." />
          </StyledBodyCell>
        </StyledTableRow>
      );
    }
    return visibleRows.map((row, index) => {
      const helpId = getTestName(row.reportFormat.testName);
      const testUnit = row.reportFormat.testUnit;
      if (isNotesOrDisclaimer(helpId, testUnit)) {
        return <></>;
      }

      const compoundMeta = compoundMetaData[helpId] || { fullname: '', nhanes: '', loq: '' };
      const labelInformation = labelInfo[row.ddfv.label] || { description: '' };
      const isLastRow = index === visibleRows.length - 1;

      return (
        <StyledTableRow hover key={index} onClick={() => handleOpen(row.ddfv)}>
          <StyledBodyCell
            sx={{
              borderBottom: isLastRow
                ? 'none'
                : `1px solid ${theme.custom.report.details.resultTable.nameCell.borderBottomColor}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0px',
                height: 'inherit',
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: '500', color: theme.custom.report.details.resultTable.nameCell.shortName.color }}
              >
                {helpId}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: '400', color: theme.custom.report.details.resultTable.nameCell.fullName.color }}
              >
                {row.ddfv.testNameOnly}
              </Typography>
            </Box>
          </StyledBodyCell>
          <StyledLevelCell>{row.ddfv?.value}</StyledLevelCell>
          <StyledRegularCell>{compoundMeta.nhanes}</StyledRegularCell>
          <StyledRegularCell>{compoundMeta.loq}</StyledRegularCell>
          <StyledRegularCell>{compoundMeta.detection_freq}</StyledRegularCell>
          <StyledRegularCell>{renderReportLabelChipWithTooltip(row.ddfv, labelInformation)}</StyledRegularCell>
        </StyledTableRow>
      );
    });
  };

  const [isCompoundDetailsLoading, setIsCompoundDetailsLoading] = useState<boolean>(false);
  const [isDescComponentOpen, setIsDescComponentOpen] = useState(false);
  const [reportCompoundDetails, setReportCompoundDetails] = useState<DisplayDetailsForValue | null>(null);

  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();

  const { apiFetchRequest } = useBackendApi();

  const fetchDetails = async () => {
    try {
      const labelResponse = await apiFetchRequest({ endpoint: `${backendURL}/help/${labelInfoHelpId}` });
      const labelData = JSON.parse(labelResponse.body.h.replace(/'/g, '"'));

      setLabelInfo(labelData);
    } catch (error) {
      console.error('Error fetching details:', error);
      addAlertMessages({ message: 'Error fetching details', severityType: 'error' });
    } finally {
      setDataReady(true);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, []);

  const handleChangePage = (_: React.MouseEvent | null, newPage: number) => {
    setPage(newPage);
  };

  const showLoadingSpinner = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          margin: '10px 0',
        }}
      >
        <GradientCircularProgress />
      </Box>
    );
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    const comparator = (a: CompoundCardProps, b: CompoundCardProps) => {
      if (orderBy === 'value') {
        if (a.value === null) return 1;
        if (b.value === null) return -1;
        return order === 'desc'
          ? parseFloat(b.value || '0') - parseFloat(a.value || '0')
          : parseFloat(a.value || '0') - parseFloat(b.value || '0');
      }
      if (orderBy === 'testName') {
        return order === 'desc'
          ? b.reportFormat.testName.localeCompare(a.reportFormat.testName)
          : a.reportFormat.testName.localeCompare(b.reportFormat.testName);
      }
      return 0;
    };
    return [...compoundDataList].sort(comparator);
  }, [order, orderBy, compoundDataList]);

  const visibleRows = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleOpen = (ddfv: DisplayDetailsForValue) => {
    setIsDescComponentOpen(true);
    setReportCompoundDetails(ddfv);
    setIsCompoundDetailsLoading(true);
  };

  const handleClose = () => {
    setIsDescComponentOpen(false);
    setReportCompoundDetails(null);
  };

  const getDetectionFrequency = () => {
    const helpId = getTestName(reportCompoundDetails?.testName || '');
    return compoundMetaData[helpId]?.detection_freq;
  };

  if (isLoading) {
    return showLoadingSpinner();
  }

  const renderLoadingCompoundDetails = () => {
    if (!isCompoundDetailsLoading) return <></>;

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.custom.report.details.resultTable.loadCompoundDetails.backgroundColor,
          opacity: 0.8,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1,
          [theme.breakpoints.up('md')]: {
            '& .MuiCircularProgress-root': {
              width: '30vh !important',
              height: '30vh !important',
            },
          },
          [theme.breakpoints.down('md')]: {
            '& .MuiCircularProgress-root': {
              width: '40vh !important',
              height: '40vh !important',
            },
          },
        }}
      >
        <GradientCircularProgressContainer loadingMessage={''} />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        padding: '8px 16px',
        backgroundColor: theme.custom.report.details.resultTable.backgroundColor,
        borderRadius: theme.custom.report.details.resultTable.borderRadius,
        boxShadow: theme.custom.report.details.resultTable.boxShadow,
      }}
    >
      {renderSnackBarAlertComponent()}
      <>
        {renderLoadingCompoundDetails()}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledHeaderCell>Name</StyledHeaderCell>
                <StyledHeaderCell>
                  <TableSortLabel
                    active={orderBy === 'value'}
                    direction={orderBy === 'value' ? order : 'asc'}
                    onClick={() => handleRequestSort('value')}
                    sx={{ color: theme.custom.report.details.resultTable.tableSort.color }}
                  >
                    Level (ng/mL)
                  </TableSortLabel>
                </StyledHeaderCell>
                <StyledHeaderCell>NHANES</StyledHeaderCell>
                <StyledHeaderCell>LOQ (ng/mL)</StyledHeaderCell>
                <StyledHeaderCell>Detection freq</StyledHeaderCell>
                <StyledHeaderCell>Detected</StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderTableBodyRows(dataReady, visibleRows, compoundMetaData, labelInfo, handleOpen)}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {page > 0 && (
          <PaginationContainer>
            <StyledPagination
              hidePrevButton
              hideNextButton
              count={Math.ceil(compoundDataList.length / rowsPerPage)}
              page={page + 1}
              onChange={(_, newPage) => handleChangePage(null, newPage - 1)}
            />
          </PaginationContainer>
        )}
      </>
      {isDescComponentOpen && !isMobile && (
        <ReportDescriptionDrawer
          compoundDataList={compoundDataList}
          detectionFrequency={getDetectionFrequency()}
          ddfv={reportCompoundDetails}
          setIsCompoundDetailsLoading={setIsCompoundDetailsLoading}
          open={isDescComponentOpen}
          onClose={handleClose}
        />
      )}
    </Box>
  );
};

export default ReportDetailsTable;
